/**
 * Centralized Error Handling System
 * Provides structured error types and codes for consistent error handling
 */

/**
 * Error codes for categorization and handling
 */
export enum ErrorCode {
  // Authentication Errors
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
  ACCOUNT_SUSPENDED = 'ACCOUNT_SUSPENDED',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  TWO_FA_REQUIRED = 'TWO_FA_REQUIRED',
  INVALID_2FA_CODE = 'INVALID_2FA_CODE',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  INVALID_TOKEN = 'INVALID_TOKEN',
  
  // Authorization Errors
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  ACCESS_DENIED = 'ACCESS_DENIED',
  FORBIDDEN = 'FORBIDDEN',
  
  // Network Errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  CONNECTION_FAILED = 'CONNECTION_FAILED',
  SSL_PINNING_FAILED = 'SSL_PINNING_FAILED',
  SERVER_UNREACHABLE = 'SERVER_UNREACHABLE',
  
  // Validation Errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  PASSWORD_TOO_WEAK = 'PASSWORD_TOO_WEAK',
  
  // Server Errors
  SERVER_ERROR = 'SERVER_ERROR',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  DATABASE_ERROR = 'DATABASE_ERROR',
  
  // Resource Errors
  NOT_FOUND = 'NOT_FOUND',
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  COMPANY_NOT_FOUND = 'COMPANY_NOT_FOUND',
  SUBMISSION_NOT_FOUND = 'SUBMISSION_NOT_FOUND',
  
  // Business Logic Errors
  DUPLICATE_ENTRY = 'DUPLICATE_ENTRY',
  CONFLICT = 'CONFLICT',
  OPERATION_FAILED = 'OPERATION_FAILED',
  INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
  LIMIT_EXCEEDED = 'LIMIT_EXCEEDED',
  
  // File/Upload Errors
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  UPLOAD_FAILED = 'UPLOAD_FAILED',
  
  // Unknown/Generic
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  LOW = 'LOW',       // Informational, user can continue
  MEDIUM = 'MEDIUM', // Warning, user should be aware
  HIGH = 'HIGH',     // Error, operation failed but app continues
  CRITICAL = 'CRITICAL', // Critical, app state may be compromised
}

/**
 * Structured error class with rich metadata
 */
export class AppError extends Error {
  code: ErrorCode;
  statusCode?: number;
  severity: ErrorSeverity;
  retryable: boolean;
  details?: Record<string, unknown>;
  timestamp: number;
  
  constructor(
    message: string,
    code: ErrorCode = ErrorCode.UNKNOWN_ERROR,
    options: {
      statusCode?: number;
      severity?: ErrorSeverity;
      retryable?: boolean;
      details?: Record<string, unknown>;
    } = {}
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.statusCode = options.statusCode;
    this.severity = options.severity || this.inferSeverity(code);
    this.retryable = options.retryable !== undefined ? options.retryable : this.inferRetryable(code);
    this.details = options.details;
    this.timestamp = Date.now();
    
    // Maintains proper stack trace for where error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
  
  /**
   * Infer severity from error code
   */
  private inferSeverity(code: ErrorCode): ErrorSeverity {
    // Critical errors
    if ([
      ErrorCode.SSL_PINNING_FAILED,
      ErrorCode.ACCOUNT_SUSPENDED,
      ErrorCode.ACCESS_DENIED,
    ].includes(code)) {
      return ErrorSeverity.CRITICAL;
    }
    
    // High severity errors
    if ([
      ErrorCode.INVALID_CREDENTIALS,
      ErrorCode.SESSION_EXPIRED,
      ErrorCode.SERVER_ERROR,
      ErrorCode.INTERNAL_SERVER_ERROR,
      ErrorCode.DATABASE_ERROR,
    ].includes(code)) {
      return ErrorSeverity.HIGH;
    }
    
    // Medium severity errors
    if ([
      ErrorCode.VALIDATION_ERROR,
      ErrorCode.INVALID_INPUT,
      ErrorCode.NOT_FOUND,
      ErrorCode.OPERATION_FAILED,
    ].includes(code)) {
      return ErrorSeverity.MEDIUM;
    }
    
    // Default to medium
    return ErrorSeverity.MEDIUM;
  }
  
  /**
   * Infer if error is retryable
   */
  private inferRetryable(code: ErrorCode): boolean {
    // Retryable errors (network/temporary issues)
    return [
      ErrorCode.NETWORK_ERROR,
      ErrorCode.TIMEOUT,
      ErrorCode.CONNECTION_FAILED,
      ErrorCode.SERVER_UNREACHABLE,
      ErrorCode.SERVICE_UNAVAILABLE,
    ].includes(code);
  }
  
  /**
   * Convert to plain object for logging
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      severity: this.severity,
      retryable: this.retryable,
      details: this.details,
      timestamp: this.timestamp,
      stack: this.stack,
    };
  }
}

/**
 * Parse HTTP status code to ErrorCode
 */
export function httpStatusToErrorCode(statusCode: number): ErrorCode {
  switch (statusCode) {
    case 400:
      return ErrorCode.VALIDATION_ERROR;
    case 401:
      return ErrorCode.INVALID_TOKEN;
    case 403:
      return ErrorCode.INSUFFICIENT_PERMISSIONS;
    case 404:
      return ErrorCode.NOT_FOUND;
    case 409:
      return ErrorCode.CONFLICT;
    case 429:
      return ErrorCode.LIMIT_EXCEEDED;
    case 500:
      return ErrorCode.INTERNAL_SERVER_ERROR;
    case 503:
      return ErrorCode.SERVICE_UNAVAILABLE;
    default:
      if (statusCode >= 500) {
        return ErrorCode.SERVER_ERROR;
      }
      return ErrorCode.UNKNOWN_ERROR;
  }
}

/**
 * Parse error from API response or caught error
 */
export function parseError(error: unknown): AppError {
  // Already an AppError
  if (error instanceof AppError) {
    return error;
  }
  
  // Network/Fetch errors
  if (error.name === 'AbortError' || error.message?.includes('aborted') || error.message?.includes('timeout')) {
    return new AppError(
      'Request timeout. Please check your connection and try again.',
      ErrorCode.TIMEOUT,
      { retryable: true }
    );
  }
  
  // Network connection errors
  if (error.message?.includes('network') || 
      error.message?.includes('fetch') || 
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('load failed') ||
      error.message?.includes('NetworkError') ||
      error.message?.includes('Network request failed')) {
    return new AppError(
      'Network connection failed. Please check your internet connection and try again.',
      ErrorCode.NETWORK_ERROR,
      { retryable: true }
    );
  }
  
  // SSL/Certificate errors
  if (error.message?.includes('SSL') || 
      error.message?.includes('certificate') || 
      error.message?.includes('pinning')) {
    return new AppError(
      'Security check failed. Please ensure you\'re on a secure network.',
      ErrorCode.SSL_PINNING_FAILED,
      { retryable: false }
    );
  }
  
  // API response errors (for libraries like axios)
  if (error.response) {
    const statusCode = error.response.status;
    const message = error.response.data?.message || error.message || 'An error occurred';
    const code = httpStatusToErrorCode(statusCode);
    
    return new AppError(message, code, {
      statusCode,
      details: error.response.data,
    });
  }
  
  // Generic errors
  return new AppError(
    error.message || 'An unknown error occurred',
    ErrorCode.UNKNOWN_ERROR,
    { details: error }
  );
}

/**
 * User-friendly error messages
 */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  // Authentication
  [ErrorCode.INVALID_CREDENTIALS]: 'Invalid email or password',
  [ErrorCode.ACCOUNT_LOCKED]: 'Account is locked. Please contact support.',
  [ErrorCode.ACCOUNT_SUSPENDED]: 'Account has been suspended. Contact support.',
  [ErrorCode.EMAIL_NOT_VERIFIED]: 'Please verify your email address',
  [ErrorCode.TWO_FA_REQUIRED]: 'Two-factor authentication required',
  [ErrorCode.INVALID_2FA_CODE]: 'Invalid verification code',
  [ErrorCode.SESSION_EXPIRED]: 'Your session has expired. Please login again.',
  [ErrorCode.INVALID_TOKEN]: 'Invalid or expired token',
  
  // Authorization
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: 'You don\'t have permission to perform this action',
  [ErrorCode.ACCESS_DENIED]: 'Access denied',
  [ErrorCode.FORBIDDEN]: 'This action is forbidden',
  
  // Network
  [ErrorCode.NETWORK_ERROR]: 'Network connection failed. Please check your internet.',
  [ErrorCode.TIMEOUT]: 'Request timeout. Please try again.',
  [ErrorCode.CONNECTION_FAILED]: 'Could not connect to server',
  [ErrorCode.SSL_PINNING_FAILED]: 'Security check failed. Please ensure you\'re on a secure network.',
  [ErrorCode.SERVER_UNREACHABLE]: 'Server is unreachable',
  
  // Validation
  [ErrorCode.VALIDATION_ERROR]: 'Please check your input',
  [ErrorCode.INVALID_INPUT]: 'Invalid input provided',
  [ErrorCode.MISSING_REQUIRED_FIELD]: 'Required field is missing',
  [ErrorCode.INVALID_EMAIL]: 'Invalid email address',
  [ErrorCode.INVALID_PASSWORD]: 'Invalid password',
  [ErrorCode.PASSWORD_TOO_WEAK]: 'Password is too weak',
  
  // Server
  [ErrorCode.SERVER_ERROR]: 'Server error occurred',
  [ErrorCode.INTERNAL_SERVER_ERROR]: 'Internal server error',
  [ErrorCode.SERVICE_UNAVAILABLE]: 'Service temporarily unavailable',
  [ErrorCode.DATABASE_ERROR]: 'Database error occurred',
  
  // Resources
  [ErrorCode.NOT_FOUND]: 'Resource not found',
  [ErrorCode.RESOURCE_NOT_FOUND]: 'Requested resource not found',
  [ErrorCode.USER_NOT_FOUND]: 'User not found',
  [ErrorCode.COMPANY_NOT_FOUND]: 'Company not found',
  [ErrorCode.SUBMISSION_NOT_FOUND]: 'Submission not found',
  
  // Business Logic
  [ErrorCode.DUPLICATE_ENTRY]: 'This entry already exists',
  [ErrorCode.CONFLICT]: 'Conflict occurred',
  [ErrorCode.OPERATION_FAILED]: 'Operation failed',
  [ErrorCode.INSUFFICIENT_BALANCE]: 'Insufficient balance',
  [ErrorCode.LIMIT_EXCEEDED]: 'Limit exceeded',
  
  // File/Upload
  [ErrorCode.FILE_TOO_LARGE]: 'File is too large',
  [ErrorCode.INVALID_FILE_TYPE]: 'Invalid file type',
  [ErrorCode.UPLOAD_FAILED]: 'Upload failed',
  
  // Unknown
  [ErrorCode.UNKNOWN_ERROR]: 'An unknown error occurred',
};

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: AppError | ErrorCode, fallback?: string): string {
  const code = error instanceof AppError ? error.code : error;
  return ERROR_MESSAGES[code] || fallback || 'An error occurred';
}

export default AppError;
